import React from "react";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import { OrderItem } from "shared/api";
import dayjs from "dayjs";
import { Routes } from "shared/routers";
import { TableButton } from "shared/ui";

export enum OrderTableColumnKey {
  Order = "order_code",
  Buyer = "buyer",
  PONumber = "po_number",
  CustomerPO = "customer_order_number",
  Production = "production_datetime",
  Delivery = "wanted_delivery_datetime",
  Amount = "quantity_expected",
  Status = "status",
  Actions = "actions",
}

export enum OrderTableColumnTitle {
  Order = "Order",
  Buyer = "Customer",
  PONumber = "PO Number",
  CustomerPO = "Customer PO",
  Production = "Production",
  Delivery = "Delivery",
  Amount = "Amount",
  Status = "Status",
  Actions = "Actions",
}

interface useGetSimpleOrderTableColumnsProps {
  showDeleteOrderModal: (record: OrderItem) => void;
}

export const useGetSimpleOrderTableColumns = ({
  showDeleteOrderModal,
}: useGetSimpleOrderTableColumnsProps): ColumnsType<OrderItem> => [
  {
    title: OrderTableColumnTitle.Order,
    dataIndex: OrderTableColumnKey.Order,
    key: OrderTableColumnKey.Order,
    render: (_: string, record: OrderItem): React.ReactNode => {
      const order = record?.order_code;
      const redirectLink = `${Routes.orders.hyperlinkUrl}/${order}`.replace(
        ":orderID/",
        ""
      );

      return (
        <Link to={redirectLink} className="text-blue-600 underline">
          {order}
        </Link>
      );
    },
  },
  {
    title: OrderTableColumnTitle.Buyer,
    dataIndex: [OrderTableColumnKey.Buyer, "name"],
    key: OrderTableColumnKey.Buyer,
    render: (_: string, record: OrderItem): React.ReactNode => {
      const buyer = record?.buyer;
      if (!buyer) return "-";

      const redirectLink = `${Routes.companies.hyperlink}/${buyer.id}`.replace(
        ":companyID/",
        ""
      );

      return (
        <Link to={redirectLink} className="text-blue-600 underline">
          {buyer.name}
        </Link>
      );
    },
  },
  {
    title: OrderTableColumnTitle.PONumber,
    dataIndex: OrderTableColumnKey.PONumber,
    key: OrderTableColumnKey.PONumber,
  },
  {
    title: OrderTableColumnTitle.CustomerPO,
    dataIndex: OrderTableColumnKey.CustomerPO,
    key: OrderTableColumnKey.CustomerPO,
  },
  {
    title: OrderTableColumnTitle.Production,
    dataIndex: OrderTableColumnKey.Production,
    key: OrderTableColumnKey.Production,
    render: (value?: string): React.ReactNode =>
      value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-",
  },
  {
    title: OrderTableColumnTitle.Delivery,
    dataIndex: OrderTableColumnKey.Delivery,
    key: OrderTableColumnKey.Delivery,
    render: (value?: string): React.ReactNode =>
      value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-",
  },
  {
    title: OrderTableColumnTitle.Amount,
    dataIndex: OrderTableColumnKey.Amount,
    key: OrderTableColumnKey.Amount,
    render: (value?: number): React.ReactNode =>
      value !== null && value !== undefined ? value : "-",
  },
  {
    title: OrderTableColumnTitle.Status,
    dataIndex: OrderTableColumnKey.Status,
    key: OrderTableColumnKey.Status,
  },
  {
    title: OrderTableColumnTitle.Actions,
    key: OrderTableColumnKey.Actions,
    width: 130,
    render: (record: OrderItem): React.ReactNode => (
      <div className="flex items-center justify-center gap-2">
        <TableButton
          type="delete"
          onClick={() => showDeleteOrderModal(record)}
        />
      </div>
    ),
  },
];
